// ─────────────────────────────────────────────────────────────────────────────
// Hiérarchie organisationnelle (organigramme).
//
// Module PUR : n'importe que des types — aucun accès Firestore — pour être
// utilisable dans les composants client ('use client') et testable en vitest.
//
// Règle du « responsable effectif » d'une personne :
//   1. `manager_email` explicite s'il est défini (et ≠ elle-même) ;
//   2. sinon le directeur de son équipe (`Team.director_email`, ≠ elle-même) ;
//   3. sinon aucun (racine potentielle de l'arbre, ex. le président).
//
// L'arbre de l'organigramme est la forêt induite par ces liens. Les cycles sont
// rejetés à l'écriture (validateManagerAssignment) et neutralisés en lecture
// (set de visités) pour qu'une donnée corrompue ne fasse jamais boucler le build.
// ─────────────────────────────────────────────────────────────────────────────

import type { Membership, Role } from '../types/firestore'

// Formes minimales acceptées par les helpers — compatibles avec User/Team mais
// aussi avec des fixtures de test légères.
export type OrgUserLike = {
  email: string
  team_id: string | null
  manager_email?: string | null
}

export type OrgTeamLike = {
  id: string
  name: string
  director_email: string | null
}

const norm = (email: string): string => email.toLowerCase().trim()

// Règle brute (sans garde d'existence) : manager explicite sinon directeur
// d'équipe. Les auto-références sont ignorées — un directeur dont
// `team.director_email` est lui-même n'a pas de responsable par défaut.
export function getEffectiveManagerEmail(
  user: OrgUserLike,
  teamById: ReadonlyMap<string, OrgTeamLike>,
): string | null {
  const self = norm(user.email)
  const explicit = user.manager_email ? norm(user.manager_email) : null
  if (explicit && explicit !== self) return explicit
  const team = user.team_id ? teamById.get(user.team_id) : undefined
  const director = team?.director_email ? norm(team.director_email) : null
  if (director && director !== self) return director
  return null
}

// Règle + garde d'existence : un `manager_email` orphelin (user supprimé ou
// re-keyé) est traité comme absent — on retombe sur le directeur d'équipe,
// lui-même soumis à la garde, puis null. Le résultat est garanti présent dans
// `usersByEmail` (clés normalisées lowercase).
export function resolveEffectiveManagerEmail(
  user: OrgUserLike,
  teamById: ReadonlyMap<string, OrgTeamLike>,
  usersByEmail: ReadonlyMap<string, OrgUserLike>,
): string | null {
  const self = norm(user.email)
  const explicit = user.manager_email ? norm(user.manager_email) : null
  if (explicit && explicit !== self && usersByEmail.has(explicit)) return explicit
  const team = user.team_id ? teamById.get(user.team_id) : undefined
  const director = team?.director_email ? norm(team.director_email) : null
  if (director && director !== self && usersByEmail.has(director)) return director
  return null
}

export type ManagerValidationError = 'self' | 'not_found' | 'cycle'

// Validation partagée (admin + directeur) avant d'écrire `manager_email`.
// `managerEmail` null = retour au défaut (toujours valide). Cycle : on remonte
// la chaîne des responsables EFFECTIFS depuis le candidat — les arêtes de
// fallback (directeur d'équipe) comptent comme de vraies arêtes de l'arbre —
// et si la cible est un ancêtre du candidat, l'assignation boucle. Le set de
// visités protège contre des données déjà corrompues.
export function validateManagerAssignment(
  targetEmail: string,
  managerEmail: string | null,
  usersByEmail: ReadonlyMap<string, OrgUserLike>,
  teamById: ReadonlyMap<string, OrgTeamLike>,
): ManagerValidationError | null {
  if (!managerEmail) return null
  const target = norm(targetEmail)
  const candidate = norm(managerEmail)
  if (candidate === target) return 'self'
  if (!usersByEmail.has(candidate)) return 'not_found'

  const visited = new Set<string>()
  let cursor: string | null = candidate
  while (cursor) {
    if (cursor === target) return 'cycle'
    if (visited.has(cursor)) break
    visited.add(cursor)
    const person = usersByEmail.get(cursor)
    cursor = person
      ? resolveEffectiveManagerEmail(person, teamById, usersByEmail)
      : null
  }
  return null
}

// ─── Arbre sérialisable (frontière serveur → client) ────────────────────────

// Entrée du builder : ligne aplatie préparée côté serveur — avatar déjà signé
// (string), lien de fiche déjà calculé. Aucune Date, aucune fonction : le tout
// traverse la frontière RSC tel quel.
export type OrgPerson = {
  email: string
  full_name: string | null
  job_title: string | null
  role: Role
  committee: Membership | null
  team_id: string | null
  manager_email: string | null
  avatar_url: string | null
  href: string
}

export type OrgNode = {
  email: string
  full_name: string | null
  job_title: string | null
  role: Role
  team_id: string | null
  team_name: string | null
  // Dirige au moins une équipe (team.director_email === email).
  is_team_director: boolean
  avatar_url: string | null
  href: string
  children: OrgChildGroup[]
  // Total récursif de subordonnés — badge « branche repliée ».
  descendant_count: number
}

// Enfants d'un nœud groupés par équipe. `team_label` null ⇒ groupe « non
// étiqueté » (enfants de la même équipe que le parent, ou sans équipe), rendu
// sans boîte d'étiquette et toujours en premier. Les groupes étiquetés
// reproduisent les boîtes d'équipe de l'organigramme de référence (directeur
// qui dirige plusieurs équipes).
export type OrgChildGroup = {
  team_id: string | null
  team_label: string | null
  children: OrgNode[]
}

export type OrgForest = {
  // Personnes sans responsable effectif avec ≥1 subordonné (le président) —
  // plusieurs racines possibles, rendues côte à côte.
  roots: OrgNode[]
  // Ni responsable effectif ni subordonné → section « Sans rattachement ».
  unattached: OrgNode[]
}

export function buildOrgForest(
  people: OrgPerson[],
  teams: OrgTeamLike[],
): OrgForest {
  const teamById = new Map<string, OrgTeamLike>(teams.map((t) => [t.id, t]))
  const usersByEmail = new Map<string, OrgPerson>(
    people.map((p) => [norm(p.email), p]),
  )
  const directorEmails = new Set<string>(
    teams.flatMap((t) => (t.director_email ? [norm(t.director_email)] : [])),
  )

  // Passe 1 — résoudre le responsable effectif de chacun et indexer les enfants.
  const childrenByManager = new Map<string, OrgPerson[]>()
  const managerOf = new Map<string, string | null>()
  for (const p of people) {
    const manager = resolveEffectiveManagerEmail(p, teamById, usersByEmail)
    managerOf.set(norm(p.email), manager)
    if (!manager) continue
    const siblings = childrenByManager.get(manager) ?? []
    siblings.push(p)
    childrenByManager.set(manager, siblings)
  }

  const byDisplayName = (a: OrgPerson, b: OrgPerson) =>
    (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email, 'fr')

  // Directeurs d'abord, puis ordre alphabétique fr.
  const byHierarchyThenName = (a: OrgPerson, b: OrgPerson) => {
    const da = directorEmails.has(norm(a.email)) ? 0 : 1
    const db = directorEmails.has(norm(b.email)) ? 0 : 1
    if (da !== db) return da - db
    return byDisplayName(a, b)
  }

  // Passe 2 — assemblage récursif. `visited` est partagé sur toute la forêt :
  // chaque personne apparaît au plus une fois et les cycles (données
  // corrompues) sont coupés au lieu de boucler.
  const visited = new Set<string>()

  function buildNode(p: OrgPerson): OrgNode {
    const key = norm(p.email)
    visited.add(key)

    const kids = (childrenByManager.get(key) ?? [])
      .filter((k) => !visited.has(norm(k.email)))
      .sort(byHierarchyThenName)
    const childNodes = kids.map(buildNode)

    // Groupement par équipe : même équipe que le parent (ou sans équipe, ou
    // équipe inconnue) → groupe non étiqueté ; sinon un groupe par équipe.
    const ungrouped: OrgNode[] = []
    const labeled = new Map<string, OrgNode[]>()
    for (const child of childNodes) {
      const team = child.team_id ? teamById.get(child.team_id) : undefined
      if (!team || child.team_id === p.team_id) {
        ungrouped.push(child)
      } else {
        const group = labeled.get(team.id) ?? []
        group.push(child)
        labeled.set(team.id, group)
      }
    }
    const children: OrgChildGroup[] = []
    if (ungrouped.length > 0) {
      children.push({ team_id: null, team_label: null, children: ungrouped })
    }
    children.push(
      ...[...labeled.entries()]
        .map(([teamId, nodes]) => ({
          team_id: teamId,
          team_label: teamById.get(teamId)?.name ?? null,
          children: nodes,
        }))
        .sort((a, b) =>
          (a.team_label ?? '').localeCompare(b.team_label ?? '', 'fr'),
        ),
    )

    const descendant_count = childNodes.reduce(
      (sum, c) => sum + c.descendant_count + 1,
      0,
    )

    return {
      email: p.email,
      full_name: p.full_name,
      job_title: p.job_title,
      role: p.role,
      team_id: p.team_id,
      team_name: p.team_id ? (teamById.get(p.team_id)?.name ?? null) : null,
      is_team_director: directorEmails.has(key),
      avatar_url: p.avatar_url,
      href: p.href,
      children,
      descendant_count,
    }
  }

  const rootPeople = people
    .filter(
      (p) =>
        !managerOf.get(norm(p.email)) &&
        (childrenByManager.get(norm(p.email))?.length ?? 0) > 0,
    )
    .sort(byHierarchyThenName)
  const unattachedPeople = people
    .filter(
      (p) =>
        !managerOf.get(norm(p.email)) &&
        (childrenByManager.get(norm(p.email))?.length ?? 0) === 0,
    )
    .sort(byDisplayName)

  const roots = rootPeople.map(buildNode)
  const unattached = unattachedPeople.map(buildNode)

  // Garde-fou : un îlot cyclique (A↔B en données corrompues) n'a ni racine ni
  // place en « Sans rattachement » — plutôt que de disparaître, il est rendu
  // comme racine supplémentaire, le set de visités coupant l'arête de retour.
  for (const p of people) {
    if (!visited.has(norm(p.email))) roots.push(buildNode(p))
  }

  // Les grandes branches d'abord (le président avant un îlot résiduel).
  roots.sort((a, b) => b.descendant_count - a.descendant_count)

  return { roots, unattached }
}

// Emails de tous les nœuds repliables (ayant des subordonnés) — utilisé par le
// bouton « Tout replier » du canvas.
export function collectExpandableEmails(roots: OrgNode[]): string[] {
  const out: string[] = []
  const walk = (node: OrgNode) => {
    if (node.descendant_count > 0) out.push(node.email)
    for (const group of node.children) group.children.forEach(walk)
  }
  roots.forEach(walk)
  return out
}

// ─── Vues filtrées & centrage (page organigramme) ───────────────────────────

// Sous-ensemble de personnes pour la « vue par équipe » : les membres de
// l'équipe PLUS son directeur (même s'il est rattaché à une autre équipe, ex.
// un directeur qui dirige plusieurs équipes). Passé tel quel à `buildOrgForest`
// — la garde d'existence de `resolveEffectiveManagerEmail` fait retomber tout
// membre dont le manager est hors sous-ensemble sur le directeur (présent), d'où
// un mini-arbre propre enraciné sur le directeur.
export function filterPeopleForTeam(
  people: OrgPerson[],
  team: OrgTeamLike,
): OrgPerson[] {
  const director = team.director_email ? norm(team.director_email) : null
  return people.filter(
    (p) =>
      p.team_id === team.id ||
      (director !== null && norm(p.email) === director),
  )
}

// Chaîne des ancêtres d'une personne dans la forêt, de la racine jusqu'au parent
// direct (la cible EXCLUSE). Emails renvoyés tels quels (`node.email`) pour
// s'apparier au Set `collapsed` du canvas. Utilisé par la recherche : déplier
// tous les ancêtres avant de centrer sur la cible. `[]` si cible absente/racine.
export function collectAncestorEmails(
  roots: OrgNode[],
  targetEmail: string,
): string[] {
  const target = norm(targetEmail)
  let found: string[] | null = null
  const walk = (node: OrgNode, trail: string[]): void => {
    if (found) return
    if (norm(node.email) === target) {
      found = trail
      return
    }
    const next = [...trail, node.email]
    for (const group of node.children) {
      for (const child of group.children) walk(child, next)
    }
  }
  for (const root of roots) walk(root, [])
  return found ?? []
}
